import { useCallback, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  ConfirmTerminalActionDialog,
  type TerminalActionConfig,
} from '../components/detail/confirm-terminal-action-dialog';
import type { ExperimentStopFinalStatus } from '../services/experiment-stop.service';
import type { ChatbotAction, ChatbotActionId } from '../types/chatbot-message.types';

/**
 * Action ids that resolve to a terminal `/stop` call. Each carries its
 * finalStatus (only CANCELLED and COMPLETED are spec-valid via /stop) plus
 * an i18n key suffix used to look up the dialog copy + default reason.
 *
 * `scale-winner` and `confirm-scaling` both finalize the experiment as
 * COMPLETED — the State-Transitions reference has no distinct "scaled"
 * terminal state. If product later adds a separate scaling endpoint, the
 * two ids can diverge here.
 */
/**
 * Typed as `Partial<...>` so an unknown action id (the `(string & {})` half
 * of `ChatbotAction['id']`) safely returns `undefined`. Without this the
 * lookup widens to "always defined" and the dispatch guard below collapses
 * into dead code per the `no-unnecessary-condition` lint rule.
 */
const TERMINAL_ACTION_CONFIGS: Partial<
  Record<ChatbotActionId, { finalStatus: ExperimentStopFinalStatus; copyKey: string }>
> = {
  end: { finalStatus: 'CANCELLED', copyKey: 'detail.stop.actions.end' },
  'end-without-scaling': {
    finalStatus: 'COMPLETED',
    copyKey: 'detail.stop.actions.endWithoutScaling',
  },
  'scale-winner': { finalStatus: 'COMPLETED', copyKey: 'detail.stop.actions.scaleWinner' },
  'confirm-scaling': {
    finalStatus: 'COMPLETED',
    copyKey: 'detail.stop.actions.confirmScaling',
  },
};

/** Toast labels for the non-terminal stubbed actions — kept until phase 4b. */
const STUBBED_ACTION_LABELS: Partial<Record<ChatbotActionId, string>> = {
  proceed: 'Proceed past Day 7 checkpoint',
  'pause-and-adjust': 'Pause and adjust experiment',
  extend: 'Extend experiment',
  done: 'Confirm extension',
};

interface UseExperimentActionsResult {
  /** Single dispatch entry point — pass to `<ChatbotMessageRenderer onAction={...} />`. */
  handleAction: (id: ChatbotAction['id']) => void;
  /** Confirm-dialog element — render this inside the chatbot panel. */
  dialog: ReactElement;
}

/**
 * Dispatches chatbot action-button clicks. Terminal-intent ids open the
 * `ConfirmTerminalActionDialog`; everything else still falls back to the
 * toast stub until backend mutations land (phase 4b). The returned `dialog`
 * is render-controlled state — consumers must mount it inside their tree so
 * the modal can mount/unmount with the panel.
 */
export function useExperimentActions(experimentId: string): UseExperimentActionsResult {
  const { t } = useTranslation('experiments');
  const [pendingConfig, setPendingConfig] = useState<TerminalActionConfig | null>(null);

  const handleAction = useCallback(
    (id: ChatbotAction['id']) => {
      const terminalCfg = TERMINAL_ACTION_CONFIGS[id as ChatbotActionId];
      if (terminalCfg) {
        setPendingConfig({
          finalStatus: terminalCfg.finalStatus,
          title: t(`${terminalCfg.copyKey}.title`),
          description: t(`${terminalCfg.copyKey}.description`),
          defaultReason: t(`${terminalCfg.copyKey}.defaultReason`),
          confirmLabel: t(`${terminalCfg.copyKey}.confirm`),
        });
        return;
      }
      const stubLabel = STUBBED_ACTION_LABELS[id as ChatbotActionId] ?? id;
      toast.info(stubLabel, {
        description: 'Stubbed in Phase 2 — backend mutation lands in Phase 4b.',
      });
    },
    [t],
  );

  // Dialog re-creates on every render (it's a JSX expression), so memoizing
  // the return object would just churn the deps. The consumer destructures
  // both values; identity stability isn't important — the dialog itself
  // gates on `pendingConfig === null` for open/closed state.
  const dialog = (
    <ConfirmTerminalActionDialog
      config={pendingConfig}
      experimentId={experimentId}
      onClose={() => {
        setPendingConfig(null);
      }}
    />
  );
  return { handleAction, dialog };
}
